import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import classNames from "classnames";
import debounce from "lodash.debounce";
import React from "react";
import type { ActionMeta, GroupBase, MultiValue, OnChangeValue, SingleValue } from "react-select";
import { color } from "..";
import t from "../i18n";
import type { InputProps } from "../Input";
import InputV2 from "../Input/InputV2";
import type { SelectProps } from "../ReactSelect";
import ReactSelect from "../ReactSelect";
import type { BaseOption } from "../ReactSelect/useFormatOptionLabel";
import { EmptyMark } from "./Td";

interface ControlProps {
  disabled: boolean;
  column: string;
  focusableRef: FocusableRef;
}
interface P<Column> {
  children: React.ReactNode;
  dataKey: string;
  column: Column;
  edited?: boolean;
  enabled: boolean;
  setEditedDataKey: (k: string | undefined) => void;
  showEmptyMark: boolean;
  renderControl: (forwardProps: ControlProps) => React.ReactNode;
}

const HEIGHT = 36;
const PADDING_H = 8;
export const EDITABLE_FORM_CONTROL_CLASS = "editable-form-control";

const DisabledWrapper = styled.div`
  width: 100%;
  display: flex;
  text-align: inherit;
  justify-content: inherit;
  align-items: center;
  padding: 0 ${PADDING_H}px;
  position: relative;
  min-height: ${HEIGHT}px;

  &::before {
    display: block;
    content: "";
    position: absolute;
    border: 2px solid transparent;
    border-radius: 4px;
    width: 100%;
    height: 100%;
    left: 0;
  }
`;
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  text-align: inherit;
  justify-content: inherit;
  align-items: center;
  position: relative;

  min-height: ${HEIGHT}px;

  &::before {
    display: block;
    content: "";
    position: absolute;
    border: 2px solid transparent;
    border-radius: 4px;
    width: 100%;
    height: 100%;
    left: 0;
  }

  :not(.edited) {
    padding: 0 ${PADDING_H}px;

    :hover::before {
      border-style: dashed;
      border-color: ${color.lightNitrogen500_Main};
    }
  }
`;

const EditableCellInput = css`
  width: 100%;
`;

type FocusableRef = React.RefObject<{ focus: () => void }>;

type MutableFocusableRef = React.MutableRefObject<{
  focus: () => void;
} | null>;

function TableEditableCell<Column extends string>({
  children,
  enabled,
  dataKey,
  column,
  edited,
  setEditedDataKey,
  showEmptyMark,
  renderControl,
}: P<Column>) {
  const focusableRef = React.useRef<{ focus: () => void }>(null);
  const focus = React.useRef(false);

  const handleDoubleClick = React.useCallback(() => {
    focus.current = true;
    setEditedDataKey(dataKey);
  }, [dataKey, setEditedDataKey]);

  React.useLayoutEffect(() => {
    if (edited)
      // cleanup when edited turns false
      return () => {
        focus.current = false;
      };
    return;
  }, [edited]);

  React.useLayoutEffect(() => {
    if (focus.current && edited) {
      const focusableEl = focusableRef.current;
      if (!focusableEl) throw new Error("Missing input ref");
      window.setTimeout(() => focusableEl.focus());
    }
  }, [edited]);

  const content = children ? <>{children}</> : showEmptyMark ? <EmptyMark /> : null;
  if (!enabled)
    return (
      <DisabledWrapper className={classNames(EDITABLE_FORM_CONTROL_CLASS, { edited })}>
        {content}
      </DisabledWrapper>
    );

  return (
    <Wrapper
      onDoubleClick={handleDoubleClick}
      className={classNames(EDITABLE_FORM_CONTROL_CLASS, { edited })}
      title={edited ? undefined : t("table.double_click_to_edit")}
    >
      {edited ? renderControl({ disabled: !enabled, column, focusableRef }) : content}
    </Wrapper>
  );
}

export const TableInputControl = React.forwardRef(
  (
    {
      column,
      focusableRef,
      validations,
      validateOnBlur,
      validateOnFocus,
      asyncValidation,
      defaultValue,
      setDirty,
      ...otherProps
    }: ControlProps &
      // non-controlled input
      Omit<InputProps, "defaultValue" | "onChange" | "value"> & {
        defaultValue: string;
        setDirty?: (dirty: boolean, column: string) => void;
        validateOnBlur?: boolean;
        validateOnFocus?: boolean;
        validations?: ((value: string) => string | undefined)[];
        asyncValidation?: (value: string) => Promise<string | undefined>;
      },
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const [value, setValue] = React.useState(defaultValue);
    const [error, setError] = React.useState<string | undefined>();
    const valueRef = React.useRef<string>(value);
    const initialValueRef = React.useRef<string>(value);

    const debounceAsyncValidation = React.useMemo(
      () =>
        asyncValidation &&
        debounce(
          (newValue: string) => {
            void asyncValidation(newValue).then(error => {
              // can have changed during async validation
              if (valueRef.current === newValue) {
                setError(error);
              }
            });
          },
          500,
          { trailing: true },
        ),
      [asyncValidation],
    );

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
      e => {
        const newValue = e.currentTarget.value ?? "";
        setValue(newValue);
        valueRef.current = newValue;
        const err = validations?.map(v => v(newValue)).find(Boolean);
        setDirty?.(initialValueRef.current !== newValue, column);
        setError(err);
        if (err === undefined && debounceAsyncValidation) {
          debounceAsyncValidation(newValue);
        }
      },
      [validations, setDirty, column, debounceAsyncValidation],
    );

    const handleValidation = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement, Element>) => {
        const newValue = event.target.value;
        valueRef.current = newValue;
        const err = validations?.map(v => v(newValue)).find(Boolean);
        setError(err);
        if (err === undefined && debounceAsyncValidation) {
          debounceAsyncValidation(newValue);
        }
      },
      [debounceAsyncValidation, validations],
    );

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = React.useCallback(
      event => {
        if (validateOnBlur) {
          handleValidation(event);
        }
      },
      [handleValidation, validateOnBlur],
    );

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = React.useCallback(
      event => {
        if (validateOnFocus) {
          handleValidation(event);
        }
      },
      [handleValidation, validateOnFocus],
    );

    return (
      <InputV2
        {...otherProps}
        error={error}
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        ref={input => {
          if (typeof ref === "function") {
            ref(input);
          } else if (ref) {
            ref.current = input;
          }
          (focusableRef as MutableFocusableRef).current = input;
        }}
        name={column}
        className={EditableCellInput}
      />
    );
  },
);

export const TableSelectControl = React.forwardRef(
  <Option extends BaseOption, IsMulti extends boolean, Group extends GroupBase<Option>>(
    {
      defaultValue,
      setDirty,
      column,
      focusableRef,
      ...otherProps
    }: ControlProps &
      // non-controlled input
      Omit<SelectProps<Option, IsMulti, Group>, "defaultValue" | "onChange" | "value"> & {
        defaultValue: MultiValue<Option> | SingleValue<Option>;
        setDirty: (dirty: boolean, column: string) => void;
      },
    ref: React.ForwardedRef<unknown>,
  ) => {
    const [value, setValue] = React.useState(defaultValue);
    const valueRef = React.useRef(value);
    const initialValueRef = React.useRef(value);

    const handleChange = React.useCallback(
      (newValue: OnChangeValue<Option, IsMulti>, _actionMeta: ActionMeta<Option>) => {
        setValue(newValue);
        valueRef.current = newValue;
        setDirty(initialValueRef.current !== newValue, column);
      },
      [setDirty, column],
    );

    return (
      <ReactSelect<Option, IsMulti, Group>
        className={EditableCellInput}
        name={otherProps.isMulti ? `${column}[]` : column}
        menuPortalTarget={window.document.body}
        ref={input => {
          if (typeof ref === "function") {
            ref(input);
          } else if (ref) {
            ref.current = input;
          }
          (focusableRef as MutableFocusableRef).current = input;
        }}
        value={value}
        onChange={handleChange}
        {...otherProps}
      />
    );
  },
);

export default React.memo(TableEditableCell) as typeof TableEditableCell;
